import * as React from 'react';
import { createPortal } from 'react-dom';
import apiService from '../../services/api';
import {
  CrossCheckClause,
  CrossCheckMatter,
  RawCrossCheckClause,
  CrossCheckDocumentType,
  mapRawClause,
  formatClauseNumberForDisplay,
} from '../../types/crossCheck';
import { CrossCheckClauseCard } from './CrossCheckClauseCard';
import { AnalyzeAgainModal, AnalyzeAgainModalVariant } from './AnalyzeAgainModal';
import { PastNegotiationSourcePdfPanel } from './PastNegotiationSourcePdfPanel';
import { PlaybookAgentBesidePanel } from './PlaybookAgentBesidePanel';
import { buildPlaybookContextFromRows } from '../../utils/playbookContext';
import {
  buildClauseAskAgentPrefix,
  clauseHasAskAgentGrounding,
  clauseNeedsAskAgentContextSourceChoice,
  combineAskAgentContext,
  getAskAgentInitialQuestion,
  getAskAgentInitialQuestionForSource,
  getAskAgentNoMatchesNotice,
  getAskAgentNoMatchesNoPlaybookNotice,
  ASK_AGENT_CONTEXT_SOURCE_QUESTION,
  type AskAgentContextSource,
  type AskAgentDomain,
} from '../../utils/clauseAskAgentContext';
import { streamAiSuggestionFromChat } from '../../utils/crossCheckAiSuggestion';
import type { PlaybookAgentChatTurn } from './PlaybookAgentBesidePanel';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import { DocumentReader } from '../../word/api/DocumentReader';
import { getClientRequestSearchText } from '../../utils/crossCheckDocumentUtils';
import { log } from '../../utils/logging';
import './CrossCheckResultsView.css';

const SA_BESIDE_SPLIT_PCT_KEY = 'courtyard-word_sa-source-beside-split-pct';
const DEFAULT_BESIDE_PDF_PCT = 47;
const MIN_BESIDE_PDF_PCT = 22;
const MAX_BESIDE_PDF_PCT = 78;

function readStoredBesidePdfPct(): number {
  try {
    const n = parseFloat(localStorage.getItem(SA_BESIDE_SPLIT_PCT_KEY) || '');
    if (Number.isFinite(n) && n >= MIN_BESIDE_PDF_PCT && n <= MAX_BESIDE_PDF_PCT) {
      return Math.round(n);
    }
  } catch {
    /* private mode */
  }
  return DEFAULT_BESIDE_PDF_PCT;
}

/** Collect unique playbook row IDs referenced by past_agreement options (Construction Loan). */
function collectAllPastAgreementPlaybookIds(clauses: CrossCheckClause[]): string[] {
  const set = new Set<string>();
  for (const c of clauses) {
    const pa = (c as any).past_agreement;
    if (!pa || !Array.isArray(pa.options)) continue;
    for (const opt of pa.options as { id?: string }[]) {
      const id = opt?.id;
      if (id && String(id).trim()) set.add(String(id).trim());
    }
  }
  return Array.from(set).sort();
}

// Tabs differ by domain:
//  - Master Cross Checks (and any non-CL flow): grouped by `documentType`.
//      'all' | 'sales_agreement' | 'appendix_a' | 'saved'
//  - Construction Loan: grouped by `matter` classification.
//      'all' | 'commercial' | 'legal' | 'other'
type DocTabId = 'all' | 'sales_agreement' | 'appendix_a' | 'saved';
type MatterTabId = 'all' | 'commercial' | 'legal' | 'other';
type TabId = DocTabId | MatterTabId;
type StepStatus = 'pending' | 'running' | 'complete' | 'error';

export interface MccBesidePdfLayoutChangeDetail {
  /** True when the SA PDF viewer is open and portaled into TaskPane’s dock column. */
  docked: boolean;
  /** PDF column width % (for TaskPane grid); same as persisted beside split. */
  pdfWidthPct: number;
}

interface CrossCheckResultsViewProps {
  subProjectId: string;
  projectId?: string;
  /**
   * When set (MCC / Construction Loan in TaskPane), the SA PDF chrome is portaled
   * here so it spans the full clauses-view height beside Project Settings.
   */
  pdfBesideDockRef?: React.RefObject<HTMLDivElement | null>;
  /** Sibling column for the vertical resize handle (TaskPane). */
  besideHandleSlotRef?: React.RefObject<HTMLDivElement | null>;
  /** Width of this container for split drag math (usually `clauses-view`). */
  besideResizeOuterRef?: React.RefObject<HTMLElement | null>;
  /** Notifies TaskPane to toggle grid layout + CSS variable for PDF column width. */
  onMccBesidePdfLayoutChange?: (detail: MccBesidePdfLayoutChangeDetail) => void;
  /** TaskPane sets true after dock + handle slots mount (enables createPortal targets). */
  besidePdfChromeInDock?: boolean;
  /** Incremented when entering MCC/CL from the solutions list — scroll workflow steps into view. */
  workflowScrollRequestToken?: number;
  /** TaskPane MCC beside: scroll root for clause list when Ask Agent / SA PDF is docked. */
  besideRightScrollRef?: React.RefObject<HTMLElement | null>;
}

/** Nearest scrollable ancestor (overflow auto/scroll with overflow content). */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export const CrossCheckResultsView: React.FC<CrossCheckResultsViewProps> = ({
  subProjectId,
  projectId: projectIdProp,
  pdfBesideDockRef,
  besideHandleSlotRef,
  besideResizeOuterRef,
  onMccBesidePdfLayoutChange,
  besidePdfChromeInDock,
  workflowScrollRequestToken = 0,
  besideRightScrollRef,
}) => {
  const [projectId, setProjectId] = React.useState<string | undefined>(projectIdProp);
  const [parentProjectName, setParentProjectName] = React.useState<string | null>(null);
  const [detectedDomain, setDetectedDomain] = React.useState<'construction-loan' | 'real-estate' | null>(null);
  const [clauses, setClauses] = React.useState<CrossCheckClause[]>([]);
  /** Start true so we never flash the default "step 1 running" strip before the first fetch. */
  const [loading, setLoading] = React.useState(() => Boolean(subProjectId));
  const [error, setError] = React.useState<string | null>(null);
  const [syncingIndices, setSyncingIndices] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<TabId>('all');
  const [updatingLockId, setUpdatingLockId] = React.useState<string | null>(null);
  const [submittedAtByClauseId, setSubmittedAtByClauseId] = React.useState<Record<string, number>>({});
  const [mutatingClauseIds, setMutatingClauseIds] = React.useState<Record<string, boolean>>({});
  const [analyzeAgainClause, setAnalyzeAgainClause] = React.useState<CrossCheckClause | null>(null);
  const [analyzeAgainVariant, setAnalyzeAgainVariant] =
    React.useState<AnalyzeAgainModalVariant>('mcc');
  const [actionBanner, setActionBanner] = React.useState<{ type: 'success' | 'error' | 'loading'; message: string } | null>(null);
  const [workflowStep, setWorkflowStep] = React.useState<number | null>(null);
  const [workflowStatus, setWorkflowStatus] = React.useState<string | null>(null);
  /** Full `cross_check_progress` from API — `status`/`workflow_step` alone miss some terminal shapes. */
  const [workflowProgressBlob, setWorkflowProgressBlob] = React.useState<Record<string, unknown> | null>(null);
  const [timerNowMs, setTimerNowMs] = React.useState<number>(Date.now());
  const stickyHeaderRef = React.useRef<HTMLDivElement | null>(null);
  const resultsMainColumnRef = React.useRef<HTMLDivElement | null>(null);
  const clauseCardHeaderRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const setClauseCardHeaderRef = React.useCallback(
    (clauseId: string) => (el: HTMLDivElement | null) => {
      if (el) clauseCardHeaderRefs.current.set(clauseId, el);
      else clauseCardHeaderRefs.current.delete(clauseId);
    },
    [],
  );
  /** Scroll target for "Identify sides / …" when entering MCC/CL from the solutions list. */
  const workflowStepsScrollAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const consumedWorkflowScrollTokenRef = React.useRef(0);
  /** Only scroll after a real tab change — Strict Mode runs mount effects twice; a one-shot "skip first" ref still fires on the second run. */
  const prevActiveTabForScrollRef = React.useRef<TabId | null>(null);
  const syncInFlightRef = React.useRef(false);
  const lastSyncedSignatureRef = React.useRef<string>('');
  const lastSyncAttemptMsRef = React.useRef<number>(0);
  // mountMs is used as the fallback start time for step1 when the component mounts
  // before the first poll arrives (prevents the timer showing 00:00 initially).
  const mountMsRef = React.useRef<number>(Date.now());
  const stepTimingRef = React.useRef<{
    step1StartMs: number | null;
    step1ElapsedSec: number;
    step2StartMs: number | null;
    step2ElapsedSec: number;
  }>({
    step1StartMs: null,
    step1ElapsedSec: 0,
    step2StartMs: null,
    step2ElapsedSec: 0,
  });

  const toRawClauseFromIdentifiedRequest = React.useCallback((req: any, idx: number): RawCrossCheckClause => {
    const paragraphIndex =
      req?.paragraph_index ??
      req?.track_changes?.paragraph_index ??
      undefined;
    const paragraphXml =
      req?.track_changes?.paragraph_xml ||
      req?.change_history?.paragraph_xml ||
      '';
    const editedText = req?.edited_text || req?.text || '';
    const originalText = req?.original_text || '';
    const clientRequestValue =
      typeof paragraphXml === 'string' && paragraphXml.trim().length > 0
        ? paragraphXml
        : editedText || originalText || '';

    return {
      id: String(req?.id ?? `identified_${idx + 1}`),
      clause_number: req?.clause_number,
      documentType: req?.documentType || 'sales_agreement',
      // Match final cross-check behavior: prefer rendering XML for Client Request.
      client_request: clientRequestValue,
      original_text: originalText,
      edited_text: editedText,
      track_changes: req?.track_changes,
      participants: req?.participants,
      module_type: req?.module_type || 'module_b',
      paragraph_index: paragraphIndex != null ? Number(paragraphIndex) : undefined,
      track_changes_interpretation: req?.track_changes_interpretation,
      refered_file: req?.refered_file,
      text: req?.text,
      side: req?.side,
      // Construction Loan groups requests by `matter` (commercial / legal /
      // other) instead of `documentType`, so it must be carried through here.
      matter: req?.matter,
    };
  }, []);

  const loadResults = React.useCallback(async (silent = false) => {
    if (!subProjectId) {
      if (!silent) setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setWorkflowStep(null);
      setWorkflowStatus(null);
      setWorkflowProgressBlob(null);
    }
    setError(null);

    if (!silent) {
      log('CrossCheckResultsView: loading results', { subProjectId });
    }

    try {
      const response = await apiService.getSubProjectDetails(subProjectId);

      if (response.success && response.data) {
        if (response.data.parent_project_id && !projectIdProp) {
          setProjectId(response.data.parent_project_id);
        }
        const pname =
          response.data.parent_project_name ||
          response.data.project_name ||
          response.data.name;
        if (typeof pname === 'string' && pname.trim()) {
          setParentProjectName(pname.trim());
        }
        // Capture the domain detected by getSubProjectDetails so that playbook
        // resolution calls are routed to the correct backend prefix.
        const responseDomain = (response as any).detected_domain;
        if (responseDomain === 'construction-loan' || responseDomain === 'real-estate') {
          setDetectedDomain(responseDomain);
        }
        const progress = response.data.cross_check_progress || null;
        const progressObj =
          progress && typeof progress === 'object' && !Array.isArray(progress)
            ? (progress as Record<string, unknown>)
            : null;
        setWorkflowProgressBlob(progressObj);
        setWorkflowStep(
          typeof progressObj?.workflow_step === 'number'
            ? progressObj.workflow_step
            : progress?.workflow_step ?? null
        );
        setWorkflowStatus(
          typeof progressObj?.status === 'string' ? progressObj.status : progress?.status ?? null
        );

        const rawCrossCheckResults: RawCrossCheckClause[] = Array.isArray(response.data.cross_checks_results)
          ? response.data.cross_checks_results
          : [];
        const rawIdentifiedRequests = Array.isArray(response.data?.client_requests_assignments_results?.requests)
          ? response.data.client_requests_assignments_results.requests
          : [];

        // Show identified requests immediately (Step 1/2) when full cross-check
        // results are not ready yet; once cross_checks_results exists, prefer it.
        const raw: RawCrossCheckClause[] = rawCrossCheckResults.length > 0
          ? rawCrossCheckResults
          : rawIdentifiedRequests.map(toRawClauseFromIdentifiedRequest);

        // Build a set of "our_side" author names from sub-project metadata.
        // Clause cards whose every participant is our_side are hidden – they
        // represent our own redlines and should never appear in the results.
        // API returns sub_project_metadata (not metadata); without this, sideAssignments was always {}.
        const meta = response.data?.sub_project_metadata ?? response.data?.metadata ?? {};
        const sideAssignments: Record<string, string> =
          meta?.side_assignments_data?.side_assignments ?? {};
        const isOurSideOnly = (clause: RawCrossCheckClause): boolean => {
          const participants: string[] = Array.isArray((clause as any).participants)
            ? (clause as any).participants
            : [];
          if (!participants.length) return false;
          return participants.every((p) => sideAssignments[p] === 'our_side');
        };

        const filtered = Object.keys(sideAssignments).length > 0
          ? raw.filter((c) => !isOurSideOnly(c))
          : raw;
        const mapped = filtered.map(mapRawClause);

        if (!silent) {
          log('CrossCheckResultsView: loaded clauses', { count: mapped.length });
        }

        // Merge with previous clauses so later polling refreshes do not downgrade
        // already-synced client-side clause numbers (e.g. "8.7.1") back to
        // backend placeholders such as "לא נמצא".
        const isUnknownClauseNumber = (value: unknown): boolean => {
          const v = String(value ?? '').trim();
          return (
            !v ||
            v === 'לא נמצא' ||
            v.toLowerCase() === 'not found' ||
            v.toLowerCase() === 'n/a' ||
            v === '-'
          );
        };

        setClauses((prev) => {
          if (!prev.length) return mapped;
          const prevById = new Map(prev.map((c) => [c.id, c]));
          const merged = mapped.map((nextClause) => {
            const prevClause = prevById.get(nextClause.id);
            if (!prevClause) return nextClause;

            const keepPrevClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              isUnknownClauseNumber(nextClause.clause_number);
            // If we already synced a concrete clause number for the same paragraph,
            // keep it authoritative over backend placeholder numbering (e.g. 4.7/6.4).
            const keepPrevSyncedClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              !isUnknownClauseNumber(nextClause.clause_number) &&
              String(prevClause.clause_number) !== String(nextClause.clause_number) &&
              prevClause.paragraph_index != null &&
              nextClause.paragraph_index != null &&
              Number(prevClause.paragraph_index) === Number(nextClause.paragraph_index);
            const keepPrevParagraphIndex =
              prevClause.paragraph_index != null && nextClause.paragraph_index == null;

            if (!keepPrevClauseNumber && !keepPrevSyncedClauseNumber && !keepPrevParagraphIndex) {
              return nextClause;
            }

            return {
              ...nextClause,
              ...((keepPrevClauseNumber || keepPrevSyncedClauseNumber) && {
                clause_number: prevClause.clause_number,
              }),
              ...(keepPrevParagraphIndex && { paragraph_index: prevClause.paragraph_index }),
            };
          });

          return merged;
        });

        // Sync paragraph indices in the document (non-blocking): find correct indices by content.
        // Guard against repeated polling-driven sync loops: only sync when source clauses changed.
        const entries = mapped
          .filter((c) => getClientRequestSearchText(c).trim().length > 0)
          .map((c) => ({ id: c.id, searchText: getClientRequestSearchText(c) }));
        const syncSignature = JSON.stringify(
          entries.map((e) => [e.id, e.searchText.slice(0, 200)])
        );
        const hasUnresolvedParagraphIndex = mapped.some(
          (c) => getClientRequestSearchText(c).trim().length > 0 && (c as any).paragraph_index == null
        );
        const canRetryUnresolvedSync =
          hasUnresolvedParagraphIndex &&
          Date.now() - lastSyncAttemptMsRef.current > 10000;
        const forceSyncForExplicitLoad = !silent;
        const shouldRunSync =
          entries.length > 0 &&
          !syncInFlightRef.current &&
          (forceSyncForExplicitLoad || syncSignature !== lastSyncedSignatureRef.current || canRetryUnresolvedSync);

        if (shouldRunSync) {
          syncInFlightRef.current = true;
          lastSyncAttemptMsRef.current = Date.now();
          lastSyncedSignatureRef.current = syncSignature;
          setSyncingIndices(true);

          DocumentReader.syncParagraphIndicesFromDocument(entries)
            .then(({ indices, clauseNumbers }) => {
              log('CrossCheckResultsView: paragraph sync', {
                matched: Object.keys(indices).length,
                entries: entries.length,
              });

              const entryIds = new Set(entries.map((e) => e.id));
              setClauses((prev) =>
                prev.map((c) => {
                  const nextIndex = indices[c.id];
                  const nextClauseNumber = clauseNumbers[c.id];
                  const wasSyncAttempted = entryIds.has(c.id);
                  const shouldClearStaleIndex = wasSyncAttempted && nextIndex === undefined;
                  const changed = nextIndex !== undefined || nextClauseNumber !== undefined || shouldClearStaleIndex;

                  if (!changed) return c;

                  return {
                    ...c,
                    ...(shouldClearStaleIndex && { paragraph_index: undefined }),
                    ...(nextIndex !== undefined && { paragraph_index: nextIndex }),
                    ...(nextClauseNumber !== undefined && { clause_number: nextClauseNumber }),
                  };
                })
              );
            })
            .catch((err) => {
              log('CrossCheckResultsView: paragraph sync failed', {
                message: (err as Error)?.message ?? String(err),
              });
              console.warn('[CrossCheckResultsView] Paragraph index sync failed:', err);
            })
            .finally(() => {
              syncInFlightRef.current = false;
              setSyncingIndices(false);
            });
        } else if (!syncInFlightRef.current) {
          setSyncingIndices(false);
        }
      } else {
        log('CrossCheckResultsView: no data from server');
        setClauses([]);
      }
    } catch (err: any) {
      log('CrossCheckResultsView: load failed', { message: err?.message ?? String(err) });
      console.error('[CrossCheckResultsView] Error loading results:', err);
      setError(err.message || 'Failed to load cross-check results');
      setClauses([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [subProjectId, toRawClauseFromIdentifiedRequest, projectIdProp]);

  const handleToggleLock = React.useCallback(async (clause: CrossCheckClause) => {
    if (!subProjectId || !clause.id) return;
    setUpdatingLockId(clause.id);
    try {
      const response = await apiService.toggleClauseLock(subProjectId, clause.id);
      if (response?.success) {
        const newLocked = !clause.lock_status?.locked;
        setClauses((prev) =>
          prev.map((c) =>
            c.id === clause.id
              ? { ...c, lock_status: { ...(c.lock_status || {}), locked: newLocked } }
              : c
          )
        );
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Toggle lock failed:', err);
    } finally {
      setUpdatingLockId(null);
    }
  }, [subProjectId]);

  const handleResponseSubmitted = React.useCallback((clauseId: string) => {
    setClauses((prev) =>
      prev.map((c) =>
        c.id === clauseId
          ? {
              ...c,
              submission_status: {
                ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                response_submitted: true,
                response_submitted_at: new Date().toISOString(),
              },
            }
          : c
      )
    );
    setSubmittedAtByClauseId((prev) => ({ ...prev, [clauseId]: Date.now() }));
  }, []);

  const applyServerCrossChecksUpdate = React.useCallback((nextCrossChecks: RawCrossCheckClause[] | undefined | null) => {
    if (!Array.isArray(nextCrossChecks)) return;

    const activeRaw = nextCrossChecks.filter((c) => !c.stale);
    const activeIds = new Set(activeRaw.map((r) => r.id));
    const mapped = activeRaw.map(mapRawClause);

    setClauses((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      mapped.forEach((updated) => {
        byId.set(updated.id, updated);
      });
      return Array.from(byId.values()).filter((c) => activeIds.has(c.id));
    });
  }, []);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    const anyErr = err as {
      response?: { data?: { message?: string; detail?: string | Array<{ msg?: string } | string> } };
      message?: string;
    };
    const detail = anyErr?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((d) => (typeof d === 'object' && d && 'msg' in d ? String(d.msg) : String(d)))
        .join('; ');
    }
    return anyErr?.response?.data?.message || anyErr?.message || fallback;
  };

  const handleMoveClause = React.useCallback(async (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => {
    if (!subProjectId) return;
    if (clause.documentType === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const isRevertToOrigin =
      !!clause.origin_document_type && target === clause.origin_document_type;

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({
      type: 'loading',
      message: isRevertToOrigin
        ? 'Restoring original category...'
        : target === 'saved_for_later'
          ? 'Moving to Saved...'
          : 'Moving and re-analyzing clause...',
    });

    try {
      const response = await apiService.moveClause(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);

      const targetLabel = target === 'sales_agreement'
        ? 'Sales Agreement'
        : target === 'appendix_a'
          ? 'Appendix A'
          : 'Saved';
      // MCC tabs are by documentType; Construction Loan tabs are by matter — skip tab jump on CL.
      if (detectedDomain !== 'construction-loan') {
        const tabKey: TabId = target === 'saved_for_later' ? 'saved' : target;
        setActiveTab(tabKey);
      }

      const reverted = (response.data as { reverted?: boolean } | undefined)?.reverted === true;
      setActionBanner({
        type: 'success',
        message: reverted
          ? `Clause restored to ${targetLabel}`
          : `Clause moved to ${targetLabel}`,
      });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate, detectedDomain]);

  const handleMoveClauseMatter = React.useCallback(async (
    clause: CrossCheckClause,
    target: CrossCheckMatter,
  ) => {
    if (!subProjectId) return;
    const currentMatter = clause.matter || 'other';
    if (currentMatter === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const targetLabel =
      target === 'commercial' ? 'Commercial' : target === 'legal' ? 'Legal' : 'Other';

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({ type: 'loading', message: `Moving to ${targetLabel}...` });

    try {
      const response = await apiService.moveClauseMatter(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActiveTab(target);
      setActionBanner({ type: 'success', message: `Clause moved to ${targetLabel}` });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause matter failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate]);

  const handleAnalyzeAgainOpen = React.useCallback((clause: CrossCheckClause) => {
    const isCl =
      detectedDomain === 'construction-loan' ||
      clause.matter === 'commercial' ||
      clause.matter === 'legal' ||
      clause.matter === 'other';
    setAnalyzeAgainVariant(isCl ? 'cl-past-negotiations' : 'mcc');
    setAnalyzeAgainClause(clause);
  }, [detectedDomain]);

  const handleAnalyzeAgainSubmit = React.useCallback(async (payload: {
    salesAgreementFeedback?: string;
    bankOfChangesFeedback?: string;
    pastNegotiationsFeedback?: string;
  }) => {
    if (!analyzeAgainClause || !subProjectId) {
      setActionBanner({ type: 'error', message: 'Missing context for re-analysis' });
      return;
    }

    const clause = analyzeAgainClause;
    const clauseId = clause.id;
    const clauseLabel = clause.clause_number ? `Clause ${formatClauseNumberForDisplay(clause.clause_number)}` : 'clause';

    // Close modal immediately so the user can keep working while re-analysis runs.
    setAnalyzeAgainClause(null);

    log('CrossCheckResultsView: analyze again submit', {
      clauseId,
      variant: analyzeAgainVariant,
      subProjectId,
    });

    setMutatingClauseIds((prev) => ({ ...prev, [clauseId]: true }));
    const isClReanalyze = analyzeAgainVariant === 'cl-past-negotiations';
    setActionBanner({
      type: 'loading',
      message: isClReanalyze
        ? `Re-analyzing past negotiations for ${clauseLabel}...`
        : `Re-analyzing ${clauseLabel}...`,
    });

    try {
      const response = isClReanalyze
        ? await apiService.reanalyzePastNegotiations(
            subProjectId,
            clauseId,
            payload.pastNegotiationsFeedback || '',
          )
        : await apiService.reanalyzeClause(subProjectId, clauseId, {
            sales_agreement_feedback: payload.salesAgreementFeedback,
            bank_of_changes_feedback: payload.bankOfChangesFeedback,
          });
      if (!response?.success) {
        throw new Error(response?.message || 'Re-analysis failed');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActionBanner({
        type: 'success',
        message: isClReanalyze
          ? `${clauseLabel} past negotiations re-analyzed`
          : `${clauseLabel} re-analyzed`,
      });
      log('CrossCheckResultsView: analyze again success', { clauseId });
    } catch (err) {
      console.error('[CrossCheckResultsView] Reanalyze clause failed:', err);
      log('CrossCheckResultsView: analyze again failed', {
        clauseId,
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clauseId];
        return next;
      });
    }
  }, [analyzeAgainClause, analyzeAgainVariant, subProjectId, applyServerCrossChecksUpdate]);

  React.useEffect(() => {
    if (!actionBanner || actionBanner.type === 'loading') return;
    const timer = window.setTimeout(() => setActionBanner(null), 5000);
    return () => window.clearTimeout(timer);
  }, [actionBanner]);

  const handleResponseUndone = React.useCallback((clauseId: string) => {
    setClauses((prev) =>
      prev.map((c) =>
        c.id === clauseId
          ? {
              ...c,
              submission_status: {
                ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                response_submitted: false,
                response_submitted_at: undefined,
              },
            }
          : c
      )
    );
    setSubmittedAtByClauseId((prev) => {
      const next = { ...prev };
      delete next[clauseId];
      return next;
    });
  }, []);

  /** Only changes when past_agreement payloads change — not on paragraph_index / sync-only updates. */
  const pastAgreementDataFingerprint = React.useMemo(
    () => clauses.map((c) => JSON.stringify((c as any).past_agreement ?? null)).join('\n'),
    [clauses]
  );

  const [playbookXmlById, setPlaybookXmlById] = React.useState<Record<string, string>>({});
  const [playbookSideById, setPlaybookSideById] = React.useState<
    Record<string, 'our_side' | 'their_side'>
  >({});
  const [playbookFileNameById, setPlaybookFileNameById] = React.useState<Record<string, string>>({});
  const [playbookFileIdById, setPlaybookFileIdById] = React.useState<Record<string, string>>({});
  const [playbookPlainTextById, setPlaybookPlainTextById] = React.useState<Record<string, string>>({});
  const [playbooksBatchLoading, setPlaybooksBatchLoading] = React.useState(false);

  const allPlaybookIdsForBatch = React.useMemo(
    () => collectAllPastAgreementPlaybookIds(clauses),
    [pastAgreementDataFingerprint]
  );

  React.useEffect(() => {
    const resolvedProjectId = projectId ?? projectIdProp;
    if (!resolvedProjectId) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookPlainTextById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    const ids = allPlaybookIdsForBatch;
    if (ids.length === 0) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookPlainTextById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    if (detectedDomain === null) {
      setPlaybooksBatchLoading(true);
      return;
    }

    let cancelled = false;
    setPlaybooksBatchLoading(true);
    void (async () => {
      try {
        const response = await apiService.getPastContractsPlaybooksByIds(
          resolvedProjectId,
          ids,
          detectedDomain ?? 'construction-loan',
        );
        if (cancelled) return;
        if (!response.success || !Array.isArray(response.data)) {
          console.warn('CrossCheckResultsView: past agreement playbooks response invalid');
          if (!cancelled) {
            setPlaybookXmlById({});
            setPlaybookSideById({});
            setPlaybookFileNameById({});
            setPlaybookFileIdById({});
          }
          return;
        }
        const rows = response.data as Array<{
          id?: string;
          paragraph_xml?: string;
          paragraph_text?: string;
          side?: string;
          source_filename?: string;
          file_id?: string;
        }>;
        const byId: Record<string, string> = {};
        const sideById: Record<string, 'our_side' | 'their_side'> = {};
        const fileNameById: Record<string, string> = {};
        const fileIdById: Record<string, string> = {};
        const plainTextById: Record<string, string> = {};
        for (const row of rows) {
          if (row?.id && row?.paragraph_xml) {
            byId[row.id] = row.paragraph_xml;
          }
          if (row?.id && (row?.side === 'our_side' || row?.side === 'their_side')) {
            sideById[row.id] = row.side;
          }
          if (row?.id) {
            if (row.source_filename?.trim()) {
              fileNameById[row.id] = row.source_filename.trim();
            }
            if (row.file_id?.trim()) {
              fileIdById[row.id] = row.file_id.trim();
            }
            if (row.paragraph_text?.trim()) {
              plainTextById[row.id] = row.paragraph_text.trim();
            }
          }
        }
        log('CrossCheckResultsView: past agreement playbooks loaded', {
          requestedIds: ids.length,
          rows: rows.length,
        });
        setPlaybookXmlById(byId);
        setPlaybookSideById(sideById);
        setPlaybookFileNameById(fileNameById);
        setPlaybookFileIdById(fileIdById);
        setPlaybookPlainTextById(plainTextById);
      } catch (e) {
        log('CrossCheckResultsView: past agreement playbooks failed', {
          message: e instanceof Error ? e.message : String(e),
        });
        if (!cancelled) {
          setPlaybookXmlById({});
          setPlaybookSideById({});
          setPlaybookFileNameById({});
          setPlaybookFileIdById({});
          setPlaybookPlainTextById({});
        }
      } finally {
        if (!cancelled) setPlaybooksBatchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, projectIdProp, allPlaybookIdsForBatch, detectedDomain]);

  // ─── Sales Agreement "Go to" split-view state ─────────────────────────────
  // When the user clicks the "Go to" badge on a clause's Sales Agreement section,
  // we fetch the SA source PDF (highlighted on the matched page) and show it in a
  // split view: PDF on the left, clause list on the right (TaskPane portaled dock).
  type SalesAgreementSourceView = {
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    buffer: ArrayBuffer | null;
    page: number;
    total: number;
    /** Original clause text — drives the panel's client-side fuzzy highlight + scroll. */
    searchText: string;
  };
  const [salesAgreementSourceView, setSalesAgreementSourceView] =
    React.useState<SalesAgreementSourceView | null>(null);

  type PlaybookAgentBesideView = {
    sessionKey: string;
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    contextBody: string;
    projectLabel: string;
    recordCount: number;
    playbookRows: PlaybookArchiveRow[];
    playbookBodyCached: string;
    askDomain: AskAgentDomain;
    initialQuestion: string | null;
    openingAssistantMessage: string | null;
    awaitingDeepResearchConsent: boolean;
    awaitingContextSourceChoice: boolean;
    contextSource: AskAgentContextSource | null;
  };
  const [playbookAgentBeside, setPlaybookAgentBeside] =
    React.useState<PlaybookAgentBesideView | null>(null);
  const playbookAgentLoadRef = React.useRef(0);
  const [agentAiStreamingClauseId, setAgentAiStreamingClauseId] = React.useState<string | null>(
    null,
  );
  const [agentSuggestedXmlByClauseId, setAgentSuggestedXmlByClauseId] = React.useState<
    Record<string, string>
  >({});

  /** Tracks the in-flight target clause id so a slow response from a previous clause
   *  doesn't overwrite the panel after the user clicked a different clause. */
  const salesAgreementSourceTargetRef = React.useRef<string | null>(null);

  /** PDF column width (%); persisted; adjusted via drag handle. */
  const [saBesidePdfWidthPct, setSaBesidePdfWidthPct] = React.useState(readStoredBesidePdfPct);
  const saBesidePdfWidthPctRef = React.useRef(saBesidePdfWidthPct);
  React.useEffect(() => {
    saBesidePdfWidthPctRef.current = saBesidePdfWidthPct;
  }, [saBesidePdfWidthPct]);

  const resultsViewRef = React.useRef<HTMLDivElement>(null);
  const besideResizeDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startPct: number;
    containerWidth: number;
  } | null>(null);

  const persistBesidePdfPct = React.useCallback((pct: number) => {
    const clamped = Math.round(
      Math.min(MAX_BESIDE_PDF_PCT, Math.max(MIN_BESIDE_PDF_PCT, pct)),
    );
    try {
      localStorage.setItem(SA_BESIDE_SPLIT_PCT_KEY, String(clamped));
    } catch {
      /* ignore */
    }
    return clamped;
  }, []);

  const handleBesideResizePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const root = besideResizeOuterRef?.current ?? resultsViewRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      besideResizeDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startPct: saBesidePdfWidthPctRef.current,
        containerWidth: Math.max(1, rect.width),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [besideResizeOuterRef],
  );

  const handleBesideResizePointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const delta = e.clientX - d.startX;
    const deltaPct = (delta / d.containerWidth) * 100;
    const next = Math.min(
      MAX_BESIDE_PDF_PCT,
      Math.max(MIN_BESIDE_PDF_PCT, d.startPct + deltaPct),
    );
    setSaBesidePdfWidthPct(Math.round(next * 10) / 10);
  }, []);

  const scheduleScrollActiveBesideClauseRef = React.useRef<() => void>(() => {});

  const endBesideResizeDrag = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    besideResizeDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === 'ArrowLeft') delta = -2;
    else if (e.key === 'ArrowRight') delta = 2;
    else if (e.key === 'Home') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MIN_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else if (e.key === 'End') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MAX_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else return;
    e.preventDefault();
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev + delta));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeDoubleClick = React.useCallback(() => {
    setSaBesidePdfWidthPct(persistBesidePdfPct(DEFAULT_BESIDE_PDF_PCT));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const mccTaskPaneBesideWire =
    !!pdfBesideDockRef && !!besideHandleSlotRef && !!onMccBesidePdfLayoutChange;

  const besidePanelOpen = !!salesAgreementSourceView || !!playbookAgentBeside;

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const docked = besidePanelOpen;
    onMccBesidePdfLayoutChange!({ docked, pdfWidthPct: saBesidePdfWidthPct });
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange, besidePanelOpen, saBesidePdfWidthPct]);

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const cb = onMccBesidePdfLayoutChange!;
    return () => {
      cb({ docked: false, pdfWidthPct: saBesidePdfWidthPctRef.current });
    };
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange]);

  React.useLayoutEffect(() => {
    if (workflowScrollRequestToken <= consumedWorkflowScrollTokenRef.current) return;
    consumedWorkflowScrollTokenRef.current = workflowScrollRequestToken;
    workflowStepsScrollAnchorRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [workflowScrollRequestToken]);

  /** After Ask Agent docks, align the active clause card header just under the sticky progress/tabs row. */
  const scrollFocusedClauseHeaderIntoView = React.useCallback(
    (clauseId: string) => {
      const headerEl = clauseCardHeaderRefs.current.get(clauseId);
      if (!headerEl) return false;

      const scrollRoot =
        besideRightScrollRef?.current ??
        resultsMainColumnRef.current ??
        findScrollableAncestor(headerEl);
      if (!scrollRoot) return false;

      const stickyEl = stickyHeaderRef.current;
      const anchorBottom = stickyEl
        ? stickyEl.getBoundingClientRect().bottom
        : scrollRoot.getBoundingClientRect().top;
      const headerTop = headerEl.getBoundingClientRect().top;
      const delta = headerTop - anchorBottom;
      if (Math.abs(delta) < 1) return true;
      scrollRoot.scrollTop += delta;
      return true;
    },
    [besideRightScrollRef],
  );

  const scheduleScrollActiveBesideClauseIntoView = React.useCallback(() => {
    const clauseId =
      playbookAgentBeside?.clauseId ?? salesAgreementSourceView?.clauseId ?? null;
    if (!clauseId || !besidePanelOpen) return;

    const run = () => scrollFocusedClauseHeaderIntoView(clauseId);
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, [
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    scrollFocusedClauseHeaderIntoView,
  ]);

  React.useEffect(() => {
    scheduleScrollActiveBesideClauseRef.current = scheduleScrollActiveBesideClauseIntoView;
  }, [scheduleScrollActiveBesideClauseIntoView]);

  const handleCloseSalesAgreementSource = React.useCallback(() => {
    salesAgreementSourceTargetRef.current = null;
    setSalesAgreementSourceView(null);
  }, []);

  const handleClosePlaybookAgent = React.useCallback(() => {
    playbookAgentLoadRef.current += 1;
    setPlaybookAgentBeside(null);
  }, []);

  const handleOpenPlaybookAgent = React.useCallback(
    async (clause: CrossCheckClause) => {
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!resolvedProjectId) {
        setActionBanner({
          type: 'error',
          message: 'Project is not linked — cannot load playbook for Ask Agent.',
        });
        return;
      }

      handleCloseSalesAgreementSource();

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;
      const askDomain: AskAgentDomain =
        detectedDomain === 'construction-loan' ? 'construction-loan' : 'real-estate';
      const projectLabel =
        parentProjectName ||
        (askDomain === 'construction-loan' ? 'Past negotiations' : 'Negotiation archive');
      const workflowHint =
        askDomain === 'construction-loan' ? ('construction_loan' as const) : ('real_estate' as const);

      const loadId = playbookAgentLoadRef.current + 1;
      playbookAgentLoadRef.current = loadId;

      const sessionKey = `${clause.id}-${Date.now()}`;
      const hasGrounding = clauseHasAskAgentGrounding(clause, askDomain);
      const needsContextSourceChoice = clauseNeedsAskAgentContextSourceChoice(clause);
      const initialQuestion =
        hasGrounding && !needsContextSourceChoice
          ? getAskAgentInitialQuestion(askDomain)
          : null;

      setPlaybookAgentBeside({
        sessionKey,
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: true,
        error: null,
        contextBody: '',
        projectLabel,
        recordCount: 0,
        playbookRows: [],
        playbookBodyCached: '',
        askDomain,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingDeepResearchConsent: false,
        awaitingContextSourceChoice: needsContextSourceChoice,
        contextSource: null,
      });

      try {
        const rows = await apiService.listPlaybooksForAgent(resolvedProjectId, workflowHint);
        if (playbookAgentLoadRef.current !== loadId) return;

        const playbookBody = rows.length
          ? buildPlaybookContextFromRows(projectLabel, rows)
          : '(no playbook archive records for this project)';

        const contextSourceForBuild: AskAgentContextSource | null = needsContextSourceChoice
          ? null
          : 'both';
        const clausePrefix = buildClauseAskAgentPrefix(
          clause,
          playbookPlainTextById,
          playbookFileNameById,
          askDomain,
          contextSourceForBuild,
        );
        const includePlaybook = !needsContextSourceChoice;
        const contextBody = combineAskAgentContext(clausePrefix, playbookBody, {
          includePlaybookArchive: includePlaybook,
          contextSource: needsContextSourceChoice ? null : 'both',
        });

        if (!rows.length && !clausePrefix.trim()) {
          setPlaybookAgentBeside({
            sessionKey,
            clauseId: clause.id,
            clauseNumberDisplay,
            loading: false,
            error: 'No clause context or playbook records available for Ask Agent.',
            contextBody: '',
            projectLabel,
            recordCount: 0,
            playbookRows: [],
            playbookBodyCached: playbookBody,
            askDomain,
            initialQuestion: null,
            openingAssistantMessage: null,
            awaitingDeepResearchConsent: false,
            awaitingContextSourceChoice: false,
            contextSource: null,
          });
          return;
        }

        const openingAssistantMessage = needsContextSourceChoice
          ? ASK_AGENT_CONTEXT_SOURCE_QUESTION
          : !hasGrounding
            ? rows.length > 0
              ? getAskAgentNoMatchesNotice(askDomain)
              : getAskAgentNoMatchesNoPlaybookNotice(askDomain)
            : null;

        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: null,
          contextBody,
          projectLabel,
          recordCount: rows.length,
          playbookRows: rows,
          playbookBodyCached: playbookBody,
          askDomain,
          initialQuestion,
          openingAssistantMessage,
          awaitingDeepResearchConsent: !hasGrounding && !needsContextSourceChoice && rows.length > 0,
          awaitingContextSourceChoice: needsContextSourceChoice,
          contextSource: null,
        });
      } catch (err: unknown) {
        if (playbookAgentLoadRef.current !== loadId) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load playbook for Ask Agent';
        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: msg,
          contextBody: '',
          projectLabel,
          recordCount: 0,
          playbookRows: [],
          playbookBodyCached: '',
          askDomain,
          initialQuestion: null,
          openingAssistantMessage: null,
          awaitingDeepResearchConsent: false,
          awaitingContextSourceChoice: false,
          contextSource: null,
        });
      }
    },
    [
      projectId,
      projectIdProp,
      parentProjectName,
      playbookPlainTextById,
      playbookFileNameById,
      detectedDomain,
      handleCloseSalesAgreementSource,
    ],
  );

  const handleChooseAskAgentContextSource = React.useCallback(
    (source: AskAgentContextSource) => {
      const agent = playbookAgentBeside;
      if (!agent?.awaitingContextSourceChoice) return;

      const clause = clauses.find((c) => c.id === agent.clauseId);
      if (!clause) return;

      const clausePrefix = buildClauseAskAgentPrefix(
        clause,
        playbookPlainTextById,
        playbookFileNameById,
        agent.askDomain,
        source,
      );
      const includePlaybook =
        source === 'past_negotiations' || source === 'both';
      const contextBody = combineAskAgentContext(clausePrefix, agent.playbookBodyCached, {
        includePlaybookArchive: includePlaybook,
        contextSource: source,
      });
      const initialQuestion = getAskAgentInitialQuestionForSource(source, agent.askDomain);

      setPlaybookAgentBeside({
        ...agent,
        loading: false,
        error: null,
        contextBody,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingContextSourceChoice: false,
        contextSource: source,
        awaitingDeepResearchConsent: false,
      });
    },
    [playbookAgentBeside, clauses, playbookPlainTextById, playbookFileNameById],
  );

  const handleApplyAgentSuggestedEdit = React.useCallback(
    async (chatMessages: PlaybookAgentChatTurn[]) => {
      const agentState = playbookAgentBeside;
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!agentState?.clauseId || !resolvedProjectId) {
        throw new Error('Project or clause is not available');
      }
      const clause = clauses.find((c) => c.id === agentState.clauseId);
      if (!clause) {
        throw new Error('Clause not found');
      }

      setAgentAiStreamingClauseId(clause.id);
      console.log('[CrossCheckResultsView] Ask Agent — apply suggested edit: BEFORE', {
        clauseId: clause.id,
        clauseNumber: clause.clause_number,
        chatTurnCount: chatMessages.length,
      });
      try {
        const suggestedXml = await streamAiSuggestionFromChat(
          resolvedProjectId,
          clause,
          chatMessages,
          {
            contextSource: agentState.contextSource,
            askDomain: agentState.askDomain,
            playbookPlainTextById,
            playbookFileNameById,
          },
        );
        console.log('[CrossCheckResultsView] Ask Agent — apply suggested edit: AFTER stream', {
          clauseId: clause.id,
          hasXml: Boolean(suggestedXml),
          xmlLen: suggestedXml?.length ?? 0,
        });
        if (!suggestedXml) {
          throw new Error('No suggestion was generated');
        }
        setAgentSuggestedXmlByClauseId((prev) => ({ ...prev, [clause.id]: suggestedXml }));
        console.log('[CrossCheckResultsView] Ask Agent — state updated with suggested XML', {
          clauseId: clause.id,
        });
      } finally {
        setAgentAiStreamingClauseId(null);
      }
    },
    [
      playbookAgentBeside,
      projectId,
      projectIdProp,
      clauses,
      playbookPlainTextById,
      playbookFileNameById,
    ],
  );

  const handleOpenSalesAgreementSource = React.useCallback(
    async (clause: CrossCheckClause, searchTextOverride?: string) => {
      const text = (searchTextOverride ?? clause.sales_agreement ?? '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!subProjectId || !text) return;

      // Toggle off when the badge is clicked on the currently-active clause.
      if (
        salesAgreementSourceView &&
        salesAgreementSourceView.clauseId === clause.id &&
        !salesAgreementSourceView.loading
      ) {
        handleCloseSalesAgreementSource();
        return;
      }

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;

      handleClosePlaybookAgent();

      salesAgreementSourceTargetRef.current = clause.id;
      setSalesAgreementSourceView({
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: true,
        error: null,
        buffer: null,
        page: 1,
        total: 1,
        searchText: text,
      });

      try {
        const { blob, highlightPage, totalPages } =
          await apiService.getSalesAgreementSourcePdf(
            subProjectId,
            text,
            detectedDomain ?? 'real-estate',
          );
        const buffer = await blob.arrayBuffer();
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: null,
          buffer,
          page: highlightPage,
          total: totalPages,
          searchText: text,
        });
      } catch (err: any) {
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        const detail =
          err?.response?.data?.detail ||
          (typeof err?.message === 'string' ? err.message : null) ||
          'Failed to load Sales Agreement source PDF';
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: typeof detail === 'string' ? detail : 'Failed to load Sales Agreement source PDF',
          buffer: null,
          page: 1,
          total: 1,
          searchText: text,
        });
      }
    },
    [
      subProjectId,
      detectedDomain,
      salesAgreementSourceView,
      handleCloseSalesAgreementSource,
      handleClosePlaybookAgent,
    ],
  );

  // If the user switches tab while the split panel is open, close it — the
  // panel is bound to a specific clause that may not be in the new tab.
  React.useEffect(() => {
    if (!salesAgreementSourceView && !playbookAgentBeside) return;
    handleCloseSalesAgreementSource();
    handleClosePlaybookAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  React.useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Keep a ref that always holds the current workflowStatus so the interval
  // callback can read the latest value without being re-created each render.
  const workflowStatusRef = React.useRef(workflowStatus);
  React.useEffect(() => {
    workflowStatusRef.current = workflowStatus;
  }, [workflowStatus]);

  // Poll while the cross-check is active.
  //
  // Design note: we intentionally do NOT stop immediately when the initial
  // status is "completed" — that status may be stale from a previous run.
  // The MCC / Construction-Loan flow calls onComplete() (which mounts this
  // component) *before* autoStartCrossCheck fires, so the backend can still
  // show the old "completed" state for several seconds while the new run is
  // being prepared.  We therefore let the interval run and decide to stop
  // only after:
  //   a) we have observed a non-terminal status followed by a terminal one
  //      (genuine completion of the new run), OR
  //   b) we have seen 3 consecutive terminal polls without any non-terminal
  //      in between (old, already-finished sub-project — stop to avoid
  //      hammering the API).
  React.useEffect(() => {
    if (!subProjectId) return;

    let stopped = false;
    let hasSeenNonTerminal = false;
    let consecutiveTerminalPolls = 0;

    const poll = async () => {
      if (stopped) return;
      await loadResults(true);

      const s = (workflowStatusRef.current || '').toLowerCase();
      const isTerminal =
        s === 'error' || s === 'failed' || s === 'complete' || s === 'completed';

      if (!isTerminal) {
        hasSeenNonTerminal = true;
        consecutiveTerminalPolls = 0;
      } else {
        consecutiveTerminalPolls += 1;
        if (hasSeenNonTerminal || consecutiveTerminalPolls >= 3) {
          stopped = true;
          clearInterval(intervalId);
        }
      }
    };

    const intervalId = setInterval(poll, 3000);
    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [subProjectId, loadResults]);

  const stepStatuses = React.useMemo((): { step0: StepStatus; step1: StepStatus; step2: StepStatus } => {
    // Avoid the default "step 1 running" before we have server state (and during explicit reload).
    if (loading) {
      return { step0: 'complete', step1: 'pending', step2: 'pending' };
    }

    const hasCrossCheckResults = clauses.some((c) => {
      if (Boolean(c.sales_agreement) || Boolean(c.bank_of_changes)) return true;
      if (Array.isArray(c.insights) && c.insights.length > 0) return true;
      const past = c.past_agreement?.options;
      if (Array.isArray(past) && past.length > 0) return true;
      return false;
    });

    const wp = workflowProgressBlob;
    const stepKey = String(wp?.step ?? '').toLowerCase();
    const isActive = wp?.is_active === true;
    const progNum =
      typeof wp?.progress === 'number'
        ? wp.progress
        : typeof wp?.progress_percentage === 'number'
          ? (wp.progress_percentage as number)
          : null;

    const normalizedStatus = (workflowStatus || '').toLowerCase();
    const workflowComplete =
      normalizedStatus === 'complete' ||
      normalizedStatus === 'completed' ||
      stepKey === 'step3_completed' ||
      (stepKey === 'complete' && !isActive) ||
      (workflowStep === 3 &&
        !isActive &&
        (progNum == null || progNum >= 100) &&
        normalizedStatus !== 'running');
    const workflowError = normalizedStatus === 'error' || normalizedStatus === 'failed';

    // step0 (Identify sides) is always complete — it finished before we arrive here
    const step0: StepStatus = 'complete';
    let step1: StepStatus = 'running'; // default: running until we learn otherwise
    let step2: StepStatus = 'pending';

    if (workflowError) {
      step2 = 'error';
      step1 = workflowStep && workflowStep >= 2 ? 'complete' : 'running';
    } else if (workflowComplete || hasCrossCheckResults) {
      step1 = 'complete';
      step2 = 'complete';
    } else if (workflowStep && workflowStep >= 3) {
      step1 = 'complete';
      step2 = 'running';
    } else if (workflowStep && workflowStep >= 2) {
      step1 = 'complete';
      step2 = 'running';
    } else if (clauses.length > 0) {
      step1 = 'complete';
      step2 = 'running';
    }
    // workflowStep === 1 or null with no cards → step1 stays 'running'

    return { step0, step1, step2 };
  }, [workflowStep, workflowStatus, workflowProgressBlob, clauses, loading]);

  // Per-step timers — always running so the clock is live from mount
  React.useEffect(() => {
    const id = setInterval(() => setTimerNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    const now = Date.now();
    const t = stepTimingRef.current;

    const applyStatus = (
      status: StepStatus,
      startKey: 'step1StartMs' | 'step2StartMs',
      elapsedKey: 'step1ElapsedSec' | 'step2ElapsedSec',
      fallbackStartMs?: number
    ) => {
      if (status === 'pending') {
        // Don't reset start if already ticking — keeps timer live if status briefly flickers
        if (!t[startKey]) t[elapsedKey] = 0;
        return;
      }
      if (status === 'running') {
        // Seed from fallback (mount time) if we've never seen this step start
        if (!t[startKey]) t[startKey] = fallbackStartMs ?? now;
        return;
      }
      // complete / error — freeze the elapsed time
      if (t[startKey]) {
        t[elapsedKey] = Math.max(1, Math.floor((now - t[startKey]!) / 1000));
        t[startKey] = null;
      }
    };

    applyStatus(stepStatuses.step1, 'step1StartMs', 'step1ElapsedSec', mountMsRef.current);
    applyStatus(stepStatuses.step2, 'step2StartMs', 'step2ElapsedSec');
  }, [stepStatuses.step1, stepStatuses.step2]);

  const formatStepSeconds = React.useCallback((totalSec: number): string => {
    const sec = Math.max(0, Math.floor(totalSec));
    const mm = String(Math.floor(sec / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, []);

  const stepTimes = React.useMemo(() => {
    const t = stepTimingRef.current;
    const now = timerNowMs;
    const step1Sec =
      t.step1StartMs != null
        ? t.step1ElapsedSec + Math.floor((now - t.step1StartMs) / 1000)
        : t.step1ElapsedSec;
    const step2Sec =
      t.step2StartMs != null
        ? t.step2ElapsedSec + Math.floor((now - t.step2StartMs) / 1000)
        : t.step2ElapsedSec;
    return {
      step1: formatStepSeconds(step1Sec),
      step2: formatStepSeconds(step2Sec),
    };
  }, [timerNowMs, formatStepSeconds]);

  const hasConstructionLoanMatter = React.useMemo(() => (
    clauses.some((c) => {
      const matter = (c as any).matter;
      return matter === 'commercial' || matter === 'legal' || matter === 'other';
    })
  ), [clauses]);

  const isConstructionLoan = detectedDomain === 'construction-loan' || hasConstructionLoanMatter;

  // Treat any clause whose `matter` is not one of the three known matter
  // values as 'other' for tab grouping purposes (handles legacy records that
  // were classified before the matter taxonomy existed).
  const matterFor = React.useCallback((c: CrossCheckClause): MatterTabId => {
    const raw = (c as any).matter;
    if (raw === 'commercial' || raw === 'legal' || raw === 'other') {
      return raw;
    }
    return 'other';
  }, []);

  const filteredClauses = React.useMemo(() => {
    if (activeTab === 'all') return clauses;
    if (isConstructionLoan) {
      return clauses.filter((c) => matterFor(c) === activeTab);
    }
    if (activeTab === 'saved') return clauses.filter((c) => c.documentType === 'saved_for_later');
    return clauses.filter((c) => c.documentType === (activeTab as CrossCheckDocumentType));
  }, [clauses, activeTab, isConstructionLoan, matterFor]);

  const counts = React.useMemo(() => ({
    all: clauses.length,
    sales_agreement: clauses.filter((c) => c.documentType === 'sales_agreement').length,
    appendix_a: clauses.filter((c) => c.documentType === 'appendix_a').length,
    saved: clauses.filter((c) => c.documentType === 'saved_for_later').length,
    commercial: clauses.filter((c) => matterFor(c) === 'commercial').length,
    legal: clauses.filter((c) => matterFor(c) === 'legal').length,
    other: clauses.filter((c) => matterFor(c) === 'other').length,
  }), [clauses, matterFor]);

  // Progress = locked OR submitted to response OR submitted to schedule (same logic as courtyard dashboard)
  const progress = React.useMemo(() => {
    const total = clauses.length;
    const current = clauses.filter((c) => {
      const isLocked = c.lock_status?.locked ?? false;
      const isSubmittedToResponse = c.submission_status?.response_submitted ?? false;
      const isSubmittedToSchedule = c.submission_status?.schedule_submitted ?? false;
      return isLocked || isSubmittedToResponse || isSubmittedToSchedule;
    }).length;
    const pct = total ? Math.round((current / total) * 100) : 0;
    return { current, total, pct };
  }, [clauses]);

  // Construction Loan classifies each request by `matter` (commercial /
  // legal / other) instead of by document type, so the tabs differ.
  const tabs: { id: TabId; label: string; cssClass: string }[] = isConstructionLoan
    ? [
        { id: 'all', label: `All (${counts.all})`, cssClass: 'cc-tab-all' },
        { id: 'commercial', label: `Commercial (${counts.commercial})`, cssClass: 'cc-tab-sales' },
        { id: 'legal', label: `Legal (${counts.legal})`, cssClass: 'cc-tab-appendix' },
        { id: 'other', label: `Other (${counts.other})`, cssClass: 'cc-tab-saved' },
      ]
    : [
        { id: 'all', label: `All (${counts.all})`, cssClass: 'cc-tab-all' },
        { id: 'sales_agreement', label: `Sales Agreement (${counts.sales_agreement})`, cssClass: 'cc-tab-sales' },
        { id: 'appendix_a', label: `Appendix A (${counts.appendix_a})`, cssClass: 'cc-tab-appendix' },
        { id: 'saved', label: `Saved (${counts.saved})`, cssClass: 'cc-tab-saved' },
      ];

  // If the detected domain switches (e.g. CL → MCC) reset the tab so we don't
  // end up sticking on a tab id that no longer exists in the new tab set.
  React.useEffect(() => {
    const allowed = new Set<TabId>(tabs.map((t) => t.id));
    if (!allowed.has(activeTab)) {
      setActiveTab('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConstructionLoan]);

  // When tab changes, scroll outer task pane so progress + tabs stay in view (single scroll: .clauses-view)
  React.useEffect(() => {
    const prev = prevActiveTabForScrollRef.current;
    if (prev === null) {
      prevActiveTabForScrollRef.current = activeTab;
      return;
    }
    if (prev === activeTab) {
      return;
    }
    prevActiveTabForScrollRef.current = activeTab;
    stickyHeaderRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [activeTab]);

  const handleTabClick = React.useCallback((tabId: TabId) => {
    setActiveTab(tabId);
  }, []);

  const useTaskPaneBesidePortalLayout =
    mccTaskPaneBesideWire && !!besidePdfChromeInDock && besidePanelOpen;
  const pdfBesideDockHost = pdfBesideDockRef?.current ?? null;
  const besideHandleSlotHost = besideHandleSlotRef?.current ?? null;
  const showBesidePortal =
    useTaskPaneBesidePortalLayout && pdfBesideDockHost != null && besideHandleSlotHost != null;

  React.useLayoutEffect(() => {
    if (!besidePanelOpen) return;
    scheduleScrollActiveBesideClauseIntoView();
  }, [
    playbookAgentBeside?.clauseId,
    playbookAgentBeside?.sessionKey,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    besidePdfChromeInDock,
    scheduleScrollActiveBesideClauseIntoView,
  ]);

  /** Re-align focused clause header after Word add-in or split-pane resize settles. */
  React.useEffect(() => {
    if (!besidePanelOpen) return;

    const observeTargets: HTMLElement[] = [];
    const scrollRoot = besideRightScrollRef?.current;
    if (scrollRoot) observeTargets.push(scrollRoot);
    if (resultsViewRef.current) observeTargets.push(resultsViewRef.current);
    if (besideResizeOuterRef?.current) observeTargets.push(besideResizeOuterRef.current);
    if (observeTargets.length === 0) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onResizeSettled = () => {
      if (besideResizeDragRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        scheduleScrollActiveBesideClauseRef.current();
      }, 160);
    };

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResizeSettled);
      observeTargets.forEach((el) => ro!.observe(el));
    }

    window.addEventListener('resize', onResizeSettled);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', onResizeSettled);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    besidePanelOpen,
    besideRightScrollRef,
    besideResizeOuterRef,
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    scheduleScrollActiveBesideClauseIntoView,
  ]);

  const besideResizeHandleEl = (
    <div
      className="cc-sa-beside-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize side panel and clause list"
      aria-valuemin={MIN_BESIDE_PDF_PCT}
      aria-valuemax={MAX_BESIDE_PDF_PCT}
      aria-valuenow={Math.round(saBesidePdfWidthPct)}
      tabIndex={0}
      title="Drag to resize. Double-click to reset. Arrow keys nudge."
      onPointerDown={handleBesideResizePointerDown}
      onPointerMove={handleBesideResizePointerMove}
      onPointerUp={endBesideResizeDrag}
      onPointerCancel={endBesideResizeDrag}
      onLostPointerCapture={() => {
        if (besideResizeDragRef.current) {
          besideResizeDragRef.current = null;
          setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
          requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
        }
      }}
      onKeyDown={handleBesideResizeKeyDown}
      onDoubleClick={handleBesideResizeDoubleClick}
    />
  );

  const renderBesideResizeHandlePortal = (portalTarget: HTMLElement | null) =>
    portalTarget ? createPortal(besideResizeHandleEl, portalTarget) : null;

  return (
    <div
      ref={resultsViewRef}
      className={`cc-results-view${
        besidePanelOpen ? ' cc-results-view--sa-source-open' : ''
      }${
        besidePanelOpen
          ? useTaskPaneBesidePortalLayout
            ? ' cc-results-view--sa-beside-portaled'
            : ' cc-results-view--sa-split-side-by-side'
          : ''
      }`}
      style={
        besidePanelOpen && !useTaskPaneBesidePortalLayout
          ? ({
              ['--cc-sa-beside-pdf-pct' as string]: `${saBesidePdfWidthPct}%`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {salesAgreementSourceView &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked"
                  role="region"
                  aria-label="Sales Agreement source PDF"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Sales Agreement
                      {salesAgreementSourceView.clauseNumberDisplay
                        ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={handleCloseSalesAgreementSource}
                        aria-label="Close Sales Agreement viewer"
                        title="Close Sales Agreement viewer"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {salesAgreementSourceView.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                      </div>
                    )}
                    {salesAgreementSourceView.error && (
                      <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                    )}
                    {salesAgreementSourceView.buffer &&
                      !salesAgreementSourceView.loading &&
                      !salesAgreementSourceView.error && (
                        <PastNegotiationSourcePdfPanel
                          pdfData={salesAgreementSourceView.buffer}
                          initialPage={salesAgreementSourceView.page}
                          totalPagesHint={salesAgreementSourceView.total}
                          searchText={salesAgreementSourceView.searchText}
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost,
            )}
            {renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side"
              role="region"
              aria-label="Sales Agreement source PDF"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Sales Agreement
                  {salesAgreementSourceView.clauseNumberDisplay
                    ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={handleCloseSalesAgreementSource}
                    aria-label="Close Sales Agreement viewer"
                    title="Close Sales Agreement viewer"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {salesAgreementSourceView.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                  </div>
                )}
                {salesAgreementSourceView.error && (
                  <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                )}
                {salesAgreementSourceView.buffer &&
                  !salesAgreementSourceView.loading &&
                  !salesAgreementSourceView.error && (
                    <PastNegotiationSourcePdfPanel
                      pdfData={salesAgreementSourceView.buffer}
                      initialPage={salesAgreementSourceView.page}
                      totalPagesHint={salesAgreementSourceView.total}
                      searchText={salesAgreementSourceView.searchText}
                    />
                  )}
              </div>
            </div>

            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      {playbookAgentBeside &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked cc-sa-source-split-panel--playbook-agent"
                  role="region"
                  aria-label="Playbook agent chat"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Ask Agent
                      {playbookAgentBeside.clauseNumberDisplay
                        ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={handleClosePlaybookAgent}
                        aria-label="Close playbook agent"
                        title="Close playbook agent"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {playbookAgentBeside.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading playbook…</p>
                      </div>
                    )}
                    {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                      <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                    )}
                    {!playbookAgentBeside.loading &&
                      !playbookAgentBeside.error &&
                      playbookAgentBeside.contextBody && (
                        <PlaybookAgentBesidePanel
                          key={playbookAgentBeside.sessionKey}
                          projectLabel={playbookAgentBeside.projectLabel}
                          recordCount={playbookAgentBeside.recordCount}
                          contextBody={playbookAgentBeside.contextBody}
                          clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                          initialQuestion={playbookAgentBeside.initialQuestion}
                          openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                          awaitingDeepResearchConsent={
                            playbookAgentBeside.awaitingDeepResearchConsent
                          }
                          awaitingContextSourceChoice={
                            playbookAgentBeside.awaitingContextSourceChoice
                          }
                          contextSource={playbookAgentBeside.contextSource}
                          onChooseContextSource={handleChooseAskAgentContextSource}
                          playbookRows={playbookAgentBeside.playbookRows}
                          projectId={projectId ?? projectIdProp ?? null}
                          playbookDomain={detectedDomain ?? 'construction-loan'}
                          onApplySuggestedEdit={handleApplyAgentSuggestedEdit}
                          isApplyingSuggestedEdit={
                            agentAiStreamingClauseId === playbookAgentBeside.clauseId
                          }
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost!,
            )}
            {!salesAgreementSourceView && renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--playbook-agent"
              role="region"
              aria-label="Playbook agent chat"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Ask Agent
                  {playbookAgentBeside.clauseNumberDisplay
                    ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={handleClosePlaybookAgent}
                    aria-label="Close playbook agent"
                    title="Close playbook agent"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {playbookAgentBeside.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading playbook…</p>
                  </div>
                )}
                {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                  <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                )}
                {!playbookAgentBeside.loading &&
                  !playbookAgentBeside.error &&
                  playbookAgentBeside.contextBody && (
                    <PlaybookAgentBesidePanel
                      key={playbookAgentBeside.sessionKey}
                      projectLabel={playbookAgentBeside.projectLabel}
                      recordCount={playbookAgentBeside.recordCount}
                      contextBody={playbookAgentBeside.contextBody}
                      clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                      initialQuestion={playbookAgentBeside.initialQuestion}
                      openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                      awaitingDeepResearchConsent={
                        playbookAgentBeside.awaitingDeepResearchConsent
                      }
                      awaitingContextSourceChoice={
                        playbookAgentBeside.awaitingContextSourceChoice
                      }
                      contextSource={playbookAgentBeside.contextSource}
                      onChooseContextSource={handleChooseAskAgentContextSource}
                      playbookRows={playbookAgentBeside.playbookRows}
                      projectId={projectId ?? projectIdProp ?? null}
                      playbookDomain={detectedDomain ?? 'construction-loan'}
                      onApplySuggestedEdit={handleApplyAgentSuggestedEdit}
                      isApplyingSuggestedEdit={
                        agentAiStreamingClauseId === playbookAgentBeside.clauseId
                      }
                    />
                  )}
              </div>
            </div>
            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      <div
        ref={resultsMainColumnRef}
        className={`cc-results-main-column${
          besidePanelOpen
            ? ` cc-results-main-column--side-by-side${
                useTaskPaneBesidePortalLayout ? ' cc-results-main-column--mcc-beside-portal' : ''
              }`
            : ' cc-results-main-column--stacked'
        }`}
      >
      {/* Scrolls away with page (same level as Project Settings in negotiator layout) */}
      <div ref={workflowStepsScrollAnchorRef} className="cc-results-steps-scroll">
        <div className="cc-run-steps-panel">
          <div className={`cc-run-step-item cc-run-${stepStatuses.step0}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>✓</span>
            <span className="cc-run-label">Identify sides</span>
            <span className="cc-run-timer" />
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step1}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step1 === 'complete' ? '✓' : stepStatuses.step1 === 'running' ? '◌' : stepStatuses.step1 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">Identify client requests</span>
            <span className="cc-run-timer">{stepTimes.step1}</span>
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step2}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step2 === 'complete' ? '✓' : stepStatuses.step2 === 'running' ? '◌' : stepStatuses.step2 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">Cross-check analysis</span>
            <span className="cc-run-timer">{stepTimes.step2}</span>
          </div>
        </div>
      </div>

      {/* Sticky: progress + tabs only (matches ClausesEditor sticky header pattern) */}
      <div className="cc-results-sticky-header" ref={stickyHeaderRef}>
        <div className="cc-results-header">
          <div className="cc-results-progress">
            <div className="cc-results-progress-bar">
              <div
                className="cc-results-progress-fill"
                style={{
                  width: progress.total > 0 ? `${progress.pct}%` : '0%',
                }}
              />
            </div>
            <div className="cc-results-progress-text">
              Progress: {progress.current}/{progress.total} ({progress.pct}%)
            </div>
          </div>
          <div className="cc-results-header-actions">
            <button
              className="cc-refresh-btn"
              onClick={() => { void loadResults(); }}
              disabled={loading}
              title="Refresh results"
            >
              &#x21BB;
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="cc-results-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`cc-tab ${tab.cssClass}${activeTab === tab.id ? ' cc-tab-active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              aria-pressed={activeTab === tab.id}
              aria-label={`Show ${tab.label}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

      </div>

      {/* Non-blocking sync indicator */}
      {syncingIndices && (
        <div className="cc-sync-banner" role="status" aria-live="polite">
          <span className="cc-sync-banner-spinner" aria-hidden />
          <span className="cc-sync-banner-text">Syncing paragraph locations…</span>
        </div>
      )}

      {/* Error */}
      {error && <div className="cc-error">{error}</div>}

      {actionBanner && (
        <div
          className={`cc-action-banner cc-action-banner--${actionBanner.type}`}
          role="status"
        >
          {actionBanner.type === 'loading' && <span className="cc-action-banner-spinner" aria-hidden />}
          <span>{actionBanner.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="cc-loading">
          <div className="cc-spinner" />
          <span className="cc-loading-text">Loading cross-check results...</span>
          <span className="cc-loading-sub">Please wait while we fetch your data</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredClauses.length === 0 && (
        <div className="cc-empty-state">
          <span className="cc-empty-icon">&#128196;</span>
          <span className="cc-empty-title">
            {clauses.length === 0
              ? 'No cross-check results yet'
              : `No clauses in ${
                  activeTab === 'sales_agreement'
                    ? 'Sales Agreement'
                    : activeTab === 'appendix_a'
                    ? 'Appendix A'
                    : activeTab === 'saved'
                    ? 'Saved'
                    : activeTab === 'commercial'
                    ? 'Commercial'
                    : activeTab === 'legal'
                    ? 'Legal'
                    : activeTab === 'other'
                    ? 'Other'
                    : ''
                } category`}
          </span>
          {clauses.length === 0 && (
            <span className="cc-empty-subtitle">
              Run cross-check in the dashboard to see results here
            </span>
          )}
        </div>
      )}

      {/* Clause cards — key by activeTab so list remounts and updates when filter changes */}
      {!loading && filteredClauses.length > 0 && (
        <div
          key={activeTab}
          className="cc-results-list"
          role="list"
        >
          {filteredClauses.map((clause, idx) => {
            const isSaSourceActive =
              !!salesAgreementSourceView && salesAgreementSourceView.clauseId === clause.id;
            const isSaSourceLoading =
              isSaSourceActive && !!salesAgreementSourceView?.loading;
            return (
              <CrossCheckClauseCard
                key={clause.id}
                clause={clause}
                index={idx}
                projectId={projectId ?? projectIdProp}
                subProjectId={subProjectId}
                onToggleLock={handleToggleLock}
                isUpdatingLock={updatingLockId === clause.id}
                onResponseSubmitted={handleResponseSubmitted}
                onResponseUndone={handleResponseUndone}
                submittedAtForClause={submittedAtByClauseId[clause.id]}
                pastAgreementPlaybookXmlById={playbookXmlById}
                pastAgreementPlaybookSideById={playbookSideById}
                pastAgreementPlaybookFileNameById={playbookFileNameById}
                pastAgreementPlaybookFileIdById={playbookFileIdById}
                pastAgreementPlaybookPlainTextById={playbookPlainTextById}
                pastAgreementPlaybooksLoading={playbooksBatchLoading}
                pastAgreementDomain={detectedDomain ?? 'construction-loan'}
                onGoToSalesAgreementSource={handleOpenSalesAgreementSource}
                isSalesAgreementSourceActive={isSaSourceActive}
                isSalesAgreementSourceLoading={isSaSourceLoading}
                kebabVariant={isConstructionLoan ? 'construction-loan' : 'mcc'}
                onMoveClause={isConstructionLoan ? undefined : handleMoveClause}
                onMoveClauseMatter={isConstructionLoan ? handleMoveClauseMatter : undefined}
                onAnalyzeAgain={handleAnalyzeAgainOpen}
                onAskAgent={projectId ?? projectIdProp ? handleOpenPlaybookAgent : undefined}
                isMutating={!!mutatingClauseIds[clause.id]}
                externalAiStreaming={agentAiStreamingClauseId === clause.id}
                externalEditedXml={agentSuggestedXmlByClauseId[clause.id] ?? null}
                cardHeaderRef={setClauseCardHeaderRef(clause.id)}
              />
            );
          })}
        </div>
      )}
      </div>

      <AnalyzeAgainModal
        clause={analyzeAgainClause}
        variant={analyzeAgainVariant}
        onCancel={() => setAnalyzeAgainClause(null)}
        onSubmit={handleAnalyzeAgainSubmit}
      />
    </div>
  );
};

export default CrossCheckResultsView;
